import prettyMilliseconds, { Options } from 'pretty-ms'

import { assert } from '../../assert/assert.js'
import { durationLikeToMs } from './durationLikeToMs.js'
import { DurationParams, durationParamsToMs } from './durationParams.js'

export interface UnixDuration {
  plus(duration: DurationLike): UnixDuration
  minus(duration: DurationLike): UnixDuration
  times(value: number): UnixDuration

  gt(value: DurationLike): boolean
  gte(value: DurationLike): boolean
  lt(value: DurationLike): boolean
  lte(value: DurationLike): boolean
  eq(value: DurationLike): boolean

  toSeconds(): bigint
  toMilliseconds(): bigint
  format(options?: Pick<Options, 'verbose'>): string
}

class UnixDurationClass implements UnixDuration {
  private readonly milliseconds: bigint

  constructor(params: Partial<DurationParams> = {}) {
    this.milliseconds = durationParamsToMs(params)
  }

  plus(duration: DurationLike): UnixDuration {
    return UnixDuration({ milliseconds: Number(this.milliseconds + durationLikeToMs(duration)) })
  }

  minus(duration: DurationLike): UnixDuration {
    return UnixDuration({ milliseconds: Number(this.milliseconds - durationLikeToMs(duration)) })
  }

  times(value: number): UnixDuration {
    assert(value >= 0, 'Cannot multiply UnixDuration by negative value')
    return UnixDuration({ milliseconds: Number(this.milliseconds) * value })
  }

  toSeconds(): bigint {
    return this.milliseconds / 1000n
  }

  toMilliseconds(): bigint {
    return this.milliseconds
  }

  format(options?: Pick<Options, 'verbose'>): string {
    return prettyMilliseconds(this.toMilliseconds(), options)
  }

  gt(value: DurationLike): boolean {
    return this.milliseconds > durationLikeToMs(value)
  }

  gte(value: DurationLike): boolean {
    return this.milliseconds >= durationLikeToMs(value)
  }

  lt(value: DurationLike): boolean {
    return this.milliseconds < durationLikeToMs(value)
  }

  lte(value: DurationLike): boolean {
    return this.milliseconds <= durationLikeToMs(value)
  }

  eq(value: DurationLike): boolean {
    return this.milliseconds === durationLikeToMs(value)
  }

  static isInstance(value: unknown): value is UnixDuration {
    return value instanceof UnixDurationClass
  }

  static fromDurationLike(value: DurationLike): UnixDuration {
    return UnixDuration({ milliseconds: Number(durationLikeToMs(value)) })
  }
}

function UnixDurationFunction(params: Partial<DurationParams> = {}): UnixDurationClass {
  return new UnixDurationClass(params)
}

interface StaticUnixDuration {
  new (params?: Partial<DurationParams>): UnixDuration
  (params?: Partial<DurationParams>): UnixDuration
  readonly prototype: UnixDuration

  isInstance(value: unknown): value is UnixDuration
  fromDurationLike(value: DurationLike): UnixDuration
}

Object.setPrototypeOf(UnixDurationFunction, UnixDurationClass)
UnixDurationFunction.prototype = UnixDurationClass.prototype

export const UnixDuration: StaticUnixDuration = Object.assign(UnixDurationFunction, UnixDurationClass)

export type DurationLike = UnixDuration | Partial<DurationParams>
